import React, { useEffect } from "react";
import { ArrowLeft, ShoppingBag, Ticket, LogIn, Sparkles, Gift, Wallet, BadgeIndianRupee, Store, CreditCard, Layers, Hand, Trophy, Info, ShieldCheck, AlertTriangle } from "lucide-react";

const LOGO = "/paras-logo.png";
const TITLE = "How Paras CashBack Works | Shop, Scratch & Discover";
const DESC = "See how Paras CashBack works for customers and merchants. Shop at participating merchants, receive a token, scratch it and discover cashback or lucky draw opportunities.";

const CUSTOMER = [
  ["Shop", "Visit a participating Paras CashBack merchant and make a qualifying purchase.", ShoppingBag],
  ["Receive a token", "The merchant issues a Paras CashBack Token according to the applicable campaign rules.", Ticket],
  ["Open your token", "Log in to Paras CashBack and open your available token.", LogIn],
  ["Scratch to reveal", "Use the interactive scratch experience to reveal your result.", Sparkles],
  ["Check your result", "Your result may be an eligible cashback reward or a lucky draw ticket, depending on the applicable campaign.", Gift],
  ["Track your reward", "Eligible cashback and reward activity can be viewed in your Paras CashBack wallet and account history.", Wallet],
  ["Redeem when eligible", "Eligible available wallet balance may be submitted for redemption according to the applicable Cashback Terms and Redemption Policy.", BadgeIndianRupee],
];

const MERCHANT = [
  ["Register", "Register as a Paras CashBack merchant.", Store],
  ["Choose a plan", "Choose an available merchant subscription plan.", CreditCard],
  ["Get token allocation", "Receive the applicable token allocation.", Layers],
  ["Issue tokens", "Issue tokens to customers for qualifying purchases according to campaign rules.", Ticket],
  ["Customers scratch", "Customers open and scratch their tokens.", Hand],
  ["Discover results", "Customers discover their cashback or lucky draw result.", Trophy],
];

function Step({ n, title, desc, Icon, accent }) {
  return (
    <div className="hover-lift flex gap-4 rounded-2xl border border-white/10 bg-gradient-to-b from-[#111827] to-[#0b111d] p-5">
      <div className="flex flex-col items-center">
        <span className={`grid h-11 w-11 place-items-center rounded-xl ${accent} `}><Icon className="h-5 w-5" /></span>
        <span className="mt-2 font-mono text-[11px] text-slate-500">{String(n).padStart(2, "0")}</span>
      </div>
      <div>
        <h3 className="font-heading text-base font-semibold">{title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{desc}</p>
      </div>
    </div>
  );
}

export default function HowItWorksPage() {
  useEffect(() => {
    const prev = document.title; document.title = TITLE;
    const m = document.querySelector('meta[name="description"]');
    const pd = m ? m.getAttribute("content") : null;
    if (m) m.setAttribute("content", DESC);
    return () => { document.title = prev; if (m && pd) m.setAttribute("content", pd); };
  }, []);

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-100" data-testid="how-it-works-page">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5">
        <a href="/" className="flex items-center gap-2"><img src={LOGO} alt="Paras CashBack" className="h-9 w-9 rounded-lg object-contain" /><span className="font-heading text-base font-extrabold">Paras Cashback™</span></a>
        <a href="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400" data-testid="hiw-back"><ArrowLeft className="h-4 w-4" /> Home</a>
      </div>

      <header className="paras-grid-bg border-y border-white/10 bg-gradient-to-br from-emerald-500/10 via-[#0b111d] to-cyan-500/10">
        <div className="mx-auto max-w-3xl px-5 py-16 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-emerald-400"><Sparkles className="h-3.5 w-3.5" /> How it works</span>
          <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">How Paras CashBack Works</h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300">Shop, receive a token, scratch to reveal — and discover an eligible cashback reward or a lucky draw ticket. Here's the full journey for customers and merchants.</p>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-16">
        <section>
          <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-500/15 text-emerald-400"><Wallet className="h-4 w-4" /></span><h2 className="font-heading text-2xl font-bold">For Customers</h2></div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {CUSTOMER.map(([t, d, I], i) => <Step key={t} n={i + 1} title={t} desc={d} Icon={I} accent="bg-emerald-500/15 text-emerald-400" />)}
          </div>
        </section>

        <section className="mt-16">
          <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg bg-cyan-500/15 text-cyan-400"><Store className="h-4 w-4" /></span><h2 className="font-heading text-2xl font-bold">For Merchants</h2></div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {MERCHANT.map(([t, d, I], i) => <Step key={t} n={i + 1} title={t} desc={d} Icon={I} accent="bg-cyan-500/15 text-cyan-400" />)}
          </div>
        </section>

        <div className="mt-14 space-y-4">
          <div className="flex gap-3 rounded-2xl border border-white/10 bg-[#111827] p-5">
            <Info className="h-5 w-5 flex-shrink-0 text-emerald-400" />
            <p className="text-sm leading-relaxed text-slate-300"><span className="font-semibold text-slate-100">Important:</span> Token issuance, cashback eligibility, reward availability and lucky draw participation are subject to the applicable campaign rules and verification requirements.</p>
          </div>
          <div className="flex gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] p-5">
            <ShieldCheck className="h-5 w-5 flex-shrink-0 text-emerald-400" />
            <p className="text-sm leading-relaxed text-slate-300"><span className="font-semibold text-emerald-300">No separate reward charge:</span> Customers are not charged a separate fee to participate in cashback or lucky draw promotional opportunities.</p>
          </div>
          <div className="flex gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/[0.05] p-5">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-400" />
            <p className="text-xs leading-relaxed text-slate-400"><span className="font-semibold text-amber-300">Disclaimer:</span> Rewards are promotional and subject to applicable terms, eligibility, availability and campaign conditions. A reward is not guaranteed on every token.</p>
          </div>
        </div>

        <div className="mt-12 text-center"><a href="/login" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-8 py-3.5 font-semibold text-slate-950 hover:bg-emerald-400" data-testid="hiw-cta">Get Started</a></div>
      </main>

      <footer className="border-t border-white/10 py-8 text-center">
        <p className="text-xs text-slate-600">© {new Date().getFullYear()} Paras Cashback™ · <a href="/about" className="hover:text-emerald-400">About</a> · <a href="/terms" className="hover:text-emerald-400">Terms</a> · <a href="/contact" className="hover:text-emerald-400">Contact</a></p>
      </footer>
    </div>
  );
}
